import { useContext, useEffect, useState } from "react";
import type { DefaultTheme } from "styled-components/macro";
import { ThemeProvider, createGlobalStyle } from "styled-components/macro";
import { QueryParamProvider } from "use-query-params";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { AG3Theme } from "./theme";
import { SWRConfig } from "swr";
import { fetcher, SwrOnError, useSupportedLanguages } from "./util/util";
import Axios, { AxiosError } from "axios";
import { Routes } from "./Routes";
import { AuthProvider } from "./components/Auth";
import { NotificationsProvider } from "./components/Notifications/NotificationsContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CartContextProvider } from "./components/quoteCart/CartContext";
import type { IStore } from "./Store";
import { Store } from "./Store";
import GoogleFontLoader from "react-google-font-loader";
import { HelmetDefault } from "./layout/shared/HelmetDefault/HelmetDefault";
import { RedirectOnInvalidRoutes } from "./components/RedirectOnInvalidRoute/RedirectOnInvalidRoute";
import { useCookies } from "react-cookie";
import { getBrowserLanguage } from "./util/util-components";
import { AnalyticsTracker } from "./components/AnalyticsTracker/AnalyticsTracker";
import { i18nFn } from "./i18n/i18nFn";
import type { i18n } from "i18next";
import { ChatBot } from "./components/ChatBot/ChatBot";
import { AgenticSearchWidget } from "./components/AgenticSearch/AgenticSearchWidget";
import { useAuthContext } from "./components/Auth";
import { UserSwitcherProvider, UserSwitcher } from "./components/UserSwitcher";

function StorefrontChatbot() {
  const { storeState } = useContext<IStore>(Store);
  const { roleIsSomeKindOfSeller } = useAuthContext();
  if (
    storeState.storefront_metadata.enable_agentic_search &&
    roleIsSomeKindOfSeller
  ) {
    return <AgenticSearchWidget />;
  }
  if (storeState.storefront_metadata.is_chatbot_configured) {
    return <ChatBot />;
  }
  return null;
}

// Import primitive token objects for GlobalStyle - these are the keys, theme provides values
// No longer directly importing token values here, GlobalStyle will use theme context.

// Helper to transform a token object into CSS custom properties string
const toCSSVariables = (tokens: Record<string, string> | undefined): string => {
  if (!tokens) return "";
  return Object.entries(tokens)
    .map(([key, value]) => `--${key}: ${value};`)
    .join("\n  ");
};

const DynamicGlobalStyle = createGlobalStyle<{ theme: DefaultTheme }>`
  :root {
    /* Static CSS variables */
    --reach-tabs: 1;

    /* Inject ALL tokens from the theme's complete token map as CSS variables */
    /* This includes primitive, semantic, and component-specific tokens */
    ${({ theme }) => toCSSVariables(theme.allTokensForTheme)}
  }

  * {
    box-sizing: border-box;
    font-family: ${AG3Theme.fontFamily};
  }
  /* Disable a warning about importing default CSS for reach tabs. */
  :root {
    --reach-tabs: 1;
  }
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
`;

export function App() {
  Axios.defaults.baseURL = "/api";
  const { storeState } = useContext<IStore>(Store);
  const { supportedLanguageOptions } = useSupportedLanguages();
  const [i18n_default, set_i18n_default] = useState<i18n>();

  const [cookies] = useCookies([`preferred-language-${storeState.slug}`]);
  const preferred: string | undefined =
    cookies[`preferred-language-${storeState.slug}`];

  useEffect(() => {
    if (storeState.tenant_id) {
      let i18n_instance = i18n_default;
      if (!i18n_instance) {
        i18n_instance = i18nFn({ tenant_id: storeState.tenant_id });
      }
      if (preferred && i18n_instance.language !== preferred) {
        i18n_instance.changeLanguage(preferred);
      }
      set_i18n_default(i18n_instance);
    }
  }, [i18n_default, preferred, storeState.tenant_id]);

  useEffect(() => {
    const onLanguageChanged = () => {
      const lang = getBrowserLanguage();
      const isLangSupported =
        supportedLanguageOptions.findIndex(({ value }) => value === lang) !==
        -1;
      if (
        isLangSupported &&
        i18n_default &&
        i18n_default.language !== lang &&
        !preferred
      ) {
        i18n_default.changeLanguage(lang);
      }
    };
    window.addEventListener("languagechange", onLanguageChanged);
    return () => {
      window.removeEventListener("languagechange", onLanguageChanged);
    };
  }, [i18n_default, preferred, supportedLanguageOptions]);

  const activeTheme = storeState.theme || AG3Theme; // Fallback to AG3Theme if storeState.theme is not yet loaded

  return (
    <ErrorBoundary>
      {storeState?.storefront_metadata.theme_object.branding
        .customFontFamily && (
        <GoogleFontLoader
          fonts={[
            {
              font: storeState.storefront_metadata.theme_object.branding
                .customFontFamily,
              weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
            },
          ]}
        />
      )}
      <SWRConfig
        value={{
          onError: SwrOnError,
          fetcher: fetcher,
          onErrorRetry(err, _key, _config, revalidate, { retryCount }) {
            const error = err as AxiosError;
            // Don't retry on 404 errors
            if (error?.response?.status === 404) {
              return;
            }
            // Don't retry more than 5 times
            if ((retryCount ?? 0) > 5) {
              return;
            }
            // Retry after 5 seconds
            setTimeout(() => {
              revalidate?.({ retryCount });
            }, 5000);
          },
        }}
      >
        <AuthProvider>
          <ThemeProvider theme={activeTheme}>
            {" "}
            {/* Main ThemeProvider wrapping everything */}
            <HelmetDefault />
            <StorefrontChatbot />
            <Router>
              <AnalyticsTracker />
              <RedirectOnInvalidRoutes>
                <NotificationsProvider>
                  <DynamicGlobalStyle />{" "}
                  {/* DynamicGlobalStyle uses the theme from ThemeProvider */}
                  <QueryParamProvider ReactRouterRoute={Route}>
                    <CartContextProvider>
                      <UserSwitcherProvider>
                        <Routes />
                        <UserSwitcher />
                      </UserSwitcherProvider>
                    </CartContextProvider>
                  </QueryParamProvider>
                </NotificationsProvider>
              </RedirectOnInvalidRoutes>
            </Router>
          </ThemeProvider>
        </AuthProvider>
      </SWRConfig>
    </ErrorBoundary>
  );
}
